import { FormikHelpers } from 'formik'

import { useMutation } from 'hooks/useMutation'
import joinErrorPaths from 'lib/error-handling/join-error-paths'

import CREATE_USER_MUTATION from './graphql/CreateUserMutation.graphql'
import { CreateUserMutation, CreateUserMutationVariables } from './graphql/__generated__/CreateUserMutation'
import GET_USER_SESSION from 'hooks/graphql/GetUserSession.graphql'
import { GetUserSession } from 'hooks/graphql/__generated__/GetUserSession'
import { UserAttributesArguments, UserSignupSourceEnum } from '../../../../../__generated__/globalTypes'

type UseSignupUserFormProps = {
  signupSource: UserSignupSourceEnum | undefined
  emailPrefillValue?: string
  localTimezone: string | undefined
  onSignupSuccess: () => void
}

const useSignupUserForm = ({
  signupSource,
  emailPrefillValue,
  localTimezone,
  onSignupSuccess
}: UseSignupUserFormProps) => {
  const initialValues = {
    firstName: '',
    lastName: '',
    email: emailPrefillValue || '',
    phone: '',
    password: '',
    timezone: localTimezone || 'Australia/Melbourne',
    termsAccepted: false
  } as UserAttributesArguments

  const [createUser] = useMutation<CreateUserMutation, CreateUserMutationVariables>(CREATE_USER_MUTATION, {
    toastOptions: {
      defaultErrorMessage: 'Failed to create user'
    }
  })

  const onSubmit = async (
    values: UserAttributesArguments,
    { setSubmitting, setFieldError }: FormikHelpers<UserAttributesArguments>
  ) => {
    setSubmitting(true)

    const attributes = {
      ...values,
      passwordConfirmation: values.password,
      signupSource: signupSource
    } as UserAttributesArguments

    const result = await createUser({
      variables: {
        input: {
          attributes
        }
      },
      update: (cache, result) => {
        if (result.data?.createUser.success) {
          cache.updateQuery<GetUserSession>({ query: GET_USER_SESSION }, data => {
            return {
              ...data,
              isGuest: false,
              currentUser: {
                ...data?.currentUser,
                ...result.data?.createUser.currentUser
              }
            } as GetUserSession
          })

          onSignupSuccess()
        }
      }
    })

    const errors = joinErrorPaths(result.data?.createUser.errors)
    if (errors && errors?.length > 0) {
      errors?.forEach(e => (e.path && e.message ? setFieldError(e.path, e.message) : ''))
    }

    setSubmitting(false)
  }

  return {
    initialValues,
    onSubmit
  }
}

export default useSignupUserForm
